<template>
    <div class="page-customized">
        <v-progress-linear
            v-if="overlay"
            absolute
            indeterminate
        ></v-progress-linear>

        <app-bar
            :title="tag.exists() ? t('tag_title_update') : t('tag_title_create')"
            back="/tags/tags"
        />

        <v-layout class="mt-n8">
            <v-card
                width="100%"
                color="transparent"
                elevation="0"
                class="pb-8"
            >
                <v-card-text class="pb-0">
                    <v-form class="mt-6">
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('tag_label_slug') }}</div>
                            <div class="horizontal-input">
                                <v-text-field
                                    v-model="tag.slug"
                                    type="text"
                                    required
                                    outlined
                                    dense
                                    :error-messages="form.errors.get('slug')"
                                ></v-text-field>
                            </div>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('tag_label_section') }}</div>
                            <div class="horizontal-input">
                                <v-select
                                    v-model="tag.tagSectionId"
                                    :items="sections"
                                    required
                                    outlined
                                    append-icon="mdi-chevron-down"
                                    dense
                                    :error-messages="form.errors.get('tag_section_id')"
                                ></v-select>
                            </div>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('tag_label_sort_order') }}</div>
                            <div class="horizontal-input">
                                <v-text-field
                                    v-model="tag.sortOrder"
                                    type="number"
                                    step="1"
                                    required
                                    outlined
                                    dense
                                    :error-messages="form.errors.get('sort_order')"
                                ></v-text-field>
                            </div>
                        </div>
                        <div class="form-group-horizontal">
                            <div class="horizontal-label">{{ t('general_action_published') }}</div>
                            <div class="horizontal-input">
                                <v-switch
                                    class="ma-0"
                                    v-model="tag.published"
                                ></v-switch>
                            </div>
                        </div>
                    </v-form>
                </v-card-text>

                <v-card-actions class="px-4">
                    <v-btn
                        :to="{ name: 'tags' }"
                        class="mr-4"
                        color="primary"
                    >{{ t('general_action_cancel') }}</v-btn>
                    <v-btn
                        :loading="form.busy"
                        :disabled="form.busy"
                        color="orange"
                        @click.prevent="submit"
                    >{{ tag.exists() ? t('general_action_save') : t('tag_title_create') }}</v-btn>
                </v-card-actions>
            </v-card>
        </v-layout>
    </div>
</template>

<script lang="ts">
    import { computed, defineComponent, onMounted, reactive, toRefs, watch } from '@vue/composition-api'
    import { useI18n } from 'vue-i18n-composable'
    import { useStore } from '@/store'
    import { useRouter } from '@/composition-api'
    import { useForm } from '@/core/form'
    import { useEnums } from '@/core/defaults'
    import { Tag } from '@/core/models/tag'
    import { TagSection } from '@/core/models/tag-section'
    import moment from 'moment/moment'
    import EditTranslationsModal from '@/components/EditTranslationsModal.vue'

    export default defineComponent({
        components: {
            EditTranslationsModal,
        },

        /**
         * Component constructor.
         *
         * @param props
         */
        setup(props) {
            const store = useStore()
            const router = useRouter()
            const i18n = useI18n()
            const id = router.currentRoute.params['id'] || undefined

            const enums = useEnums()

            const state = reactive({
                overlay: true
            })

            const tagModel = computed(() => <Tag>store.getters['tag/get'])
            const sections = computed(
                () => [{ text: '-', value: '' }].concat(
                    store.getters['tagSectionCollection/get'].data.map((item: TagSection) => {
                        return { text: item.name, value: item.id }
                    })
                )
            )

            const form = reactive(useForm())

            const capture = () => {
                store.dispatch('tag/getOrNew', { id: id, payload: { published: '' } }).then(() => {
                    state.overlay = false
                })
            }

            const submit = () => {
                form.usingSubmit(store.dispatch('tag/updateOrCreate', {
                    id: id,
                    payload: tagModel.value.payload()
                }))
                    .then(() => {
                        const id = <any>tagModel.value.id
                        const next = () => router.push({ name: 'tags.view', params: <any>{ id: id } })
                        store.dispatch('notify', { color: 'success', message: i18n.t('general_notice_data_updated') })
                        next()
                    })
            }

            onMounted(() => {
                store.dispatch('tagSectionCollection/get', { sort: '-id' })
                capture()
            })

            return {
                form,
                capture,
                submit,
                tag: tagModel.value,
                sections,
                moment,
                ...enums,
                ...useI18n(),
                ...toRefs(state)
            }
        }
    })
</script>
